'use client';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  ChevronRight,
  FolderPlus,
  Images,
  Loader2,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import {
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type ReactNode,
  type Ref,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

type ProjectsToolbarProps = {
  isProjectsSectionExpanded: boolean;
  onToggleProjectsSection: () => void;
  allProjectsExpanded: boolean;
  onToggleAllProjects: () => void;
  /** Omitted on surfaces without a project search (e.g. the /threads page). */
  onOpenProjectSearch?: () => void;
  onOpenOrganizer: () => void;
  onCreateProject: () => void;
};

/** Native button props a wrapping trigger (e.g. a Radix `asChild` menu
 * trigger) passes through; motion-owned handlers are excluded. */
type HoverLabelPassThroughProps = Omit<
  ComponentPropsWithoutRef<'button'>,
  | 'children'
  | 'onAnimationStart'
  | 'onDrag'
  | 'onDragEnd'
  | 'onDragStart'
  | 'onClick'
  | 'disabled'
>;

export function HoverLabelIconButton({
  label,
  ariaLabel,
  onClick,
  disabled = false,
  labelClassName,
  children,
  ref,
  onFocus,
  onBlur,
  ...passThrough
}: {
  label: string;
  ariaLabel: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  labelClassName?: string;
  children: ReactNode;
  ref?: Ref<HTMLButtonElement>;
} & HoverLabelPassThroughProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedWidth, setExpandedWidth] = useState(24);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const labelRef = useRef<HTMLSpanElement | null>(null);
  const prefersReducedMotion = useReducedMotion();

  useLayoutEffect(() => {
    const labelElement = labelRef.current;
    if (!labelElement) return;
    setExpandedWidth(Math.max(24, 36 + Math.ceil(labelElement.offsetWidth)));
  }, [label]);

  return (
    <Button
      asChild
      size="icon"
      variant="ghost"
      className="text-muted-foreground hover:bg-primary/5 hover:text-foreground relative h-6 w-6 min-w-6 justify-start overflow-hidden rounded-md px-1.5"
    >
      <motion.button
        {...passThrough}
        ref={(node: HTMLButtonElement | null) => {
          buttonRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        type="button"
        onClick={onClick}
        disabled={disabled}
        onHoverStart={() => setIsExpanded(true)}
        onHoverEnd={() => setIsExpanded(false)}
        onFocus={(event) => {
          setIsExpanded(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setIsExpanded(false);
          onBlur?.(event);
        }}
        aria-label={ariaLabel}
        initial={false}
        animate={{ width: isExpanded ? expandedWidth : 24 }}
        transition={
          prefersReducedMotion
            ? { duration: 0 }
            : { type: 'spring', duration: 0.35, bounce: 0 }
        }
      >
        {children}
        <motion.span
          ref={labelRef}
          initial={false}
          animate={{ opacity: isExpanded ? 1 : 0, x: isExpanded ? 0 : -4 }}
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : { duration: 0.2, ease: 'easeOut' }
          }
          data-hover-label
          aria-hidden={!isExpanded}
          className={cn(
            'text-muted-foreground pointer-events-none absolute left-7 text-xs whitespace-nowrap',
            labelClassName
          )}
        >
          {label}
        </motion.span>
      </motion.button>
    </Button>
  );
}

export function ProjectsToolbar({
  isProjectsSectionExpanded,
  onToggleProjectsSection,
  allProjectsExpanded: _allProjectsExpanded,
  onToggleAllProjects: _onToggleAllProjects,
  onOpenProjectSearch,
  onOpenOrganizer,
  onCreateProject,
}: ProjectsToolbarProps) {
  const t = useTranslations('WorkspaceShell');

  return (
    <div
      data-sidebar-section="projects"
      className="group/projects-toolbar flex h-7 items-center justify-between px-2"
    >
      <div className="flex min-w-0 flex-1 items-center gap-0.5">
        <button
          type="button"
          onClick={onToggleProjectsSection}
          aria-expanded={isProjectsSectionExpanded}
          aria-label={
            isProjectsSectionExpanded
              ? t('collapseSection', { title: t('projects') })
              : t('expandSection', { title: t('projects') })
          }
          title={
            isProjectsSectionExpanded
              ? t('collapseSection', { title: t('projects') })
              : t('expandSection', { title: t('projects') })
          }
          className="hover:text-foreground text-muted-foreground group/projects-heading flex w-full min-w-0 cursor-pointer items-center gap-1 rounded py-0.5 pr-1 text-left text-xs font-semibold tracking-wide uppercase transition-colors"
        >
          <span className="truncate">{t('projects')}</span>
          <ChevronRight
            className={cn(
              'size-3 shrink-0 opacity-0 transition-[opacity,transform] duration-150 group-focus-within/projects-heading:opacity-100 group-hover/projects-heading:opacity-100',
              isProjectsSectionExpanded && 'rotate-90'
            )}
          />
        </button>
      </div>
      <div className="flex items-center gap-1">
        {/* Temporarily hidden: expand/collapse all projects control.
        <Button
          size="icon"
          variant="ghost"
          className="text-muted-foreground hover:bg-primary/5 hover:text-foreground h-6 w-6 rounded-md"
          onClick={onToggleAllProjects}
          disabled={!isProjectsSectionExpanded}
          aria-label={allProjectsExpanded ? t('collapseAll') : t('expandAll')}
          title={allProjectsExpanded ? t('collapseAll') : t('expandAll')}
        >
          {allProjectsExpanded ? (
            <Minimize2 className="!size-3" />
          ) : (
            <Maximize2 className="!size-3" />
          )}
        </Button>
        */}
        <HoverLabelIconButton
          label={t('organize')}
          onClick={onOpenOrganizer}
          ariaLabel={t('organizeProjects')}
        >
          <SlidersHorizontal className="text-muted-foreground size-3.5" />
        </HoverLabelIconButton>
        <HoverLabelIconButton
          label={t('newProject')}
          onClick={onCreateProject}
          ariaLabel={t('newProject')}
        >
          <FolderPlus className="text-muted-foreground size-3.5" />
        </HoverLabelIconButton>
        {onOpenProjectSearch ? (
          <HoverLabelIconButton
            label={t('search')}
            onClick={onOpenProjectSearch}
            ariaLabel={t('searchProjects')}
          >
            <Search className="text-muted-foreground size-3.5" />
          </HoverLabelIconButton>
        ) : null}
      </div>
    </div>
  );
}

export type ChatsSectionTab = 'chats' | 'notifications';

export type ChatsSectionTabs = {
  active: ChatsSectionTab;
  hasPendingNotification: boolean;
  onChange: (tab: ChatsSectionTab) => void;
};

/** The Chats | Activity switch at the head of the sidebar body. */
function ChatsSectionTabList({
  tabs,
  onSelect,
}: {
  tabs: ChatsSectionTabs;
  onSelect: (tab: ChatsSectionTab) => void;
}) {
  const t = useTranslations('WorkspaceShell');
  const tabClass = (selected: boolean) =>
    cn(
      'focus-visible:ring-ring relative flex h-6 items-center gap-1.5 rounded px-1.5 text-xs font-medium transition-colors outline-none focus-visible:ring-2',
      selected
        ? 'bg-foreground/10 text-foreground'
        : 'text-muted-foreground hover:text-foreground'
    );
  return (
    <div
      role="tablist"
      aria-label={t('chats')}
      data-sidebar-chats-tabs
      className="bg-foreground/5 -ms-2 flex items-center gap-0.5 rounded-md p-0.5"
    >
      <button
        type="button"
        role="tab"
        aria-selected={tabs.active === 'chats'}
        className={tabClass(tabs.active === 'chats')}
        onClick={() => onSelect('chats')}
      >
        {t('chats')}
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={tabs.active === 'notifications'}
        className={tabClass(tabs.active === 'notifications')}
        onClick={() => onSelect('notifications')}
      >
        {t('activity')}
        {tabs.hasPendingNotification ? (
          <span
            aria-hidden="true"
            data-notification-pending-dot
            className="bg-destructive size-1.5 rounded-full"
          />
        ) : null}
      </button>
    </div>
  );
}

/** Chat-list tools (media library, search) that expand on hover. */
function ChatTools({
  onOpenSearch,
  onOpenLibrary,
  className,
}: {
  onOpenSearch?: () => void;
  onOpenLibrary?: () => void;
  className?: string;
}) {
  const t = useTranslations('WorkspaceShell');
  return (
    <div className={cn('flex items-center gap-1', className)}>
      {onOpenLibrary && (
        <HoverLabelIconButton
          label={t('myLibrary')}
          onClick={onOpenLibrary}
          ariaLabel={t('openLibrary')}
        >
          <Images className="text-muted-foreground size-3.5" />
        </HoverLabelIconButton>
      )}
      {onOpenSearch && (
        <HoverLabelIconButton
          label={t('search')}
          onClick={onOpenSearch}
          ariaLabel={t('searchChats')}
        >
          <Search className="text-muted-foreground size-3.5" />
        </HoverLabelIconButton>
      )}
    </div>
  );
}

/**
 * Heads the sidebar body: Chats shows Pinned, Projects and Chats below it;
 * Activity swaps them for the inbox. The chat tools sit at the end on the
 * sidebar's own background, so their hover labels expand over the tabs.
 */
export function SidebarBodyTabs({
  tabs,
  onOpenSearch,
  onOpenLibrary,
}: {
  tabs: ChatsSectionTabs;
  onOpenSearch?: () => void;
  onOpenLibrary?: () => void;
}) {
  return (
    <div
      data-sidebar-body-tabs
      className="relative flex h-8 items-center justify-between gap-1.5 px-2"
    >
      <ChatsSectionTabList tabs={tabs} onSelect={tabs.onChange} />
      {tabs.active === 'chats' ? (
        <ChatTools
          onOpenSearch={onOpenSearch}
          onOpenLibrary={onOpenLibrary}
          className="absolute inset-y-0 end-2 bg-[var(--thread-sidebar)] ps-1"
        />
      ) : null}
    </div>
  );
}

export function ChatsSectionHeader({
  onOpenSearch,
  onOpenLibrary,
  isExpanded,
  onToggle,
}: {
  onOpenSearch?: () => void;
  onOpenLibrary?: () => void;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const t = useTranslations('WorkspaceShell');
  return (
    <div
      data-sidebar-section="chats"
      className="group/chats-toolbar flex h-7 items-center justify-between gap-1.5 px-2"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="text-muted-foreground hover:text-foreground group/chats-heading flex flex-1 items-center gap-1 text-left text-xs font-semibold tracking-wide uppercase"
      >
        {t('chats')}
        <ChevronRight
          className={cn(
            'size-3 opacity-0 transition-[opacity,transform] duration-150 group-focus-within/chats-heading:opacity-100 group-hover/chats-heading:opacity-100',
            isExpanded && 'rotate-90'
          )}
        />
      </button>
      <ChatTools onOpenSearch={onOpenSearch} onOpenLibrary={onOpenLibrary} />
    </div>
  );
}

export function FavoritesSectionHeader({
  isPending,
  isExpanded,
  onToggle,
}: {
  isPending?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const t = useTranslations('WorkspaceShell');
  return (
    <div
      data-sidebar-section="pinned"
      className="flex h-7 items-center justify-between gap-1.5 px-2"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="text-muted-foreground hover:text-foreground group/pinned-heading flex flex-1 items-center gap-1 text-left text-xs font-semibold tracking-wide uppercase"
      >
        {t('favorites')}
        <ChevronRight
          className={cn(
            'size-3 opacity-0 transition-[opacity,transform] duration-150 group-focus-within/pinned-heading:opacity-100 group-hover/pinned-heading:opacity-100',
            isExpanded && 'rotate-90'
          )}
        />
      </button>
      {isPending && (
        <Loader2 className="text-muted-foreground h-3 w-3 animate-spin" />
      )}
    </div>
  );
}

// type RootThreadsListProps
